import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDomain } from "@/hooks/useDomain";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Globe, X } from "lucide-react";

const LOCALES = [
  { code: "de", label: "Deutsch" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

const Navigation = () => {
  const { t, locale, setLocale } = useLanguage();
  const domain = useDomain();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Brand initial used in the square logo tile — first letter of brandName.
  const brandInitial = (domain.brandName || "Mallorca Digital").trim().charAt(0).toUpperCase();

  // On focused (non-main) domains we replace the global anchor nav with deep links
  // to the relevant service detail page so the brand stays focused.
  const focusedLinks = domain.focusedServiceSlug
    ? [
        {
          id: "service-focus",
          label: t("nav.services"),
          href: `/services/${domain.focusedServiceSlug}`,
          route: true,
        },
        { id: "blog", label: t("nav.blog"), href: "/blog", route: true },
        { id: "contact", label: t("nav.contact"), href: "/#contact" },
      ]
    : null;

  const mainLinks = [
    { id: "services", label: t("nav.services"), href: "/#services" },
    { id: "starlink", label: t("nav.starlink"), href: "/#starlink" },
    { id: "why", label: t("nav.why"), href: "/#why" },
    { id: "blog", label: t("nav.blog"), href: "/blog", route: true },
    { id: "contact", label: t("nav.contact"), href: "/#contact" },
  ];

  const links = focusedLinks || mainLinks;
  const ctaHref = domain.focusedServiceSlug
    ? `/services/${domain.focusedServiceSlug}#service-anfrage`
    : "/#contact";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="container-px mx-auto max-w-7xl">
        <div
          className={`flex items-center justify-between rounded-full px-5 lg:px-6 transition-all duration-300 ${
            scrolled
              ? "glass shadow-[0_8px_32px_rgba(0,0,0,0.06)] py-3"
              : "py-3 bg-white/40 backdrop-blur-md border border-white/40"
          }`}
        >
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 min-w-0 flex-shrink"
          >
            <div className="shrink-0 w-9 h-9 rounded-xl bg-neutral-900 text-white grid place-items-center font-display font-semibold text-base">
              {brandInitial}
            </div>
            <div className="leading-tight min-w-0">
              <div
                data-testid="logo-brand-name"
                className="font-display text-[15px] font-medium tracking-tight truncate"
              >
                {domain.brandName}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-sky-700 truncate hidden sm:block">
                {domain.brandTagline}
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.id}
                to={l.href}
                data-testid={`nav-link-${l.id}`}
                className="text-sm text-neutral-700 hover:text-neutral-950 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="language-toggle"
                  className="rounded-full gap-2 text-neutral-700 hover:bg-white/60"
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase font-medium">{locale}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                {LOCALES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`language-option-${l.code}`}
                    onClick={() => setLocale(l.code)}
                    className={`cursor-pointer ${
                      locale === l.code ? "font-semibold text-sky-700" : ""
                    }`}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to={ctaHref}
              data-testid="nav-cta-btn"
              className="hidden md:inline-flex items-center rounded-full bg-neutral-900 text-white text-sm font-medium px-5 py-2.5 hover:bg-black transition-colors"
            >
              {t("nav.cta")}
            </Link>

            <button
              className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-neutral-200 bg-white/70"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="lg:hidden mt-2 glass rounded-3xl p-6 shadow-lg"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-3">
              {links.map((l) => (
                <Link
                  key={l.id}
                  to={l.href}
                  className="text-neutral-800 text-base py-1.5"
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to={ctaHref}
                onClick={() => setMobileOpen(false)}
                className="mt-2 text-center rounded-full bg-neutral-900 text-white px-5 py-3 text-sm font-medium"
                data-testid="mobile-nav-cta"
              >
                {t("nav.cta")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
